import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import {
  Button,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Form,
  message,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import useDatasource from '../../../../hooks/useDatasource';
import useDictionaries from '../../../../hooks/useDictionaries';

import { getTitlesById } from '../../../../services/accounts/accounts';

import Column from '../../../../helpers/columns';
import POSButton from '../../POSButton';
import POSContext from '../../POSContext';
import {
  blockCard,
  cancelOrder,
  changeAttachmentsToOrder,
  changeCardToOrder,
  enableOrderCard,
  getEduInfo,
  getAvailableProducts,
  getRouteById,
  moveOrderToNextStep,
  rechargeProductToOrder,
  removeProductFromOrder,
  getAdminUnits,
} from '../../../../services/pos/pos';

import { findAll } from '../../../../services/taxonomies/blacklist-reason';
import { getUserTypesLight } from '../../../../services/taxonomies/user-types';
import { getPosDocTypes } from '../../../../services/taxonomies/doc-types';
import { getProductById } from '../../../../services/offers/products';
import AccountDetailsForm from './AccountDetailsForm';
import DatePickerLocale from '../../../DatePickerLocale';
import {
  BlockingPlaces,
  UnblockingPlaces,
} from '../../../blacklist-reasons/constants';
import { ArticleTypes } from '../../../orders/Constants';
import Restriction from '../../Restriction';
import { Environments } from '../../../../helpers/constants';
import { EduInfoCard } from '../../components/EduInfoCard';

const dictionaries = {
  blacklistReasons: findAll,
  userTypes: getUserTypesLight,
  docTypes: getPosDocTypes,
};

const MAPPING = [
  { val: 'value1', opt: 'option1' },
  { val: 'value3', opt: 'option2' },
  { val: 'value5', opt: 'option3' },
  { val: 'value7', opt: 'option4' },
];

const AccountDetails = () => {
  const { t } = useTranslation();
  const [seeEnableCard, setSeeEnableCard] = useState(false);
  const [isStocksEnabledString] = useState(window._env_.STOCKS_ENABLE);

  const isStocksEnabled = isStocksEnabledString === 'true';
  const phoneRegex = useMemo(() => /^(?:\+?\d{10,14}|[-])$/, []);

  const [form] = Form.useForm();
  const [productForm] = Form.useForm();
  const {
    shift,
    order,
    setOrder,
    cardBlocked,
    setCardBlocked,
    attachmentForm,
  } = useContext(POSContext);
  const [currentProduct, setCurrentProduct] = useState();
  const [extendedTitle, setExtendedTitle] = useState();
  const [accountCurrentTitles, setAccountCurrentTitles] = useState([]);
  const { account, card } = order;
  const [availableProductsList, setAvailableProductsList] = useState([]);
  const { attachments } = account;
  const [eduInfo, setEduInfo] = useState({});
  const environment = useMemo(() => window._env_.ENVIROMENT, []);
  const isEduEnabledString = useMemo(() => window._env_.EDU_ENABLE, []);
  const isEduEnabled = isEduEnabledString === 'true';

  const [{ blacklistReasons, docTypes, userTypes }] =
    useDictionaries(dictionaries);

  const scanEnableValue = window._env_.SCAN_ENABLE;
  const scanEnable = scanEnableValue === 'true';

  const userType = useMemo(() => {
    return userTypes?.content?.find(
      (prod) => prod?.id === order?.account?.userProfileTypeId,
    );
  }, [userTypes?.content, order?.account?.userProfileTypeId]);

  dayjs.extend(customParseFormat);

  const [line, setLine] = useState(null);
  const [whereIsRestriction, setWhereIsRestriction] = useState([]);

  // eslint-disable-next-line arrow-body-style
  const disabledDate = (current) => {
    // Can not select days before today and today
    return (
      current < moment() ||
      Date.parse(current) > Date.parse(moment().add(90, 'days'))
    );
  };

  useEffect(() => {
    getAvailableProducts(shift, order)
      .then((prod) => {
        setAvailableProductsList(prod);
      })
      .catch((err) => {
        console.error(err);
        setAvailableProductsList([]);
      });
  }, [shift, order]);

  const fetchTitles = useCallback(
    (arg) =>
      getTitlesById(account.accountId, { ...arg, pageSize: 1000 }).then(
        (response) => {
          response?.content?.forEach((title) => {
            if (title.productType === 'PURSE') {
              // eslint-disable-next-line no-param-reassign
              title.quantity = `${parseFloat(title.purseValue / 100).toFixed(
                2,
              )} RON`;
            }
          });
          // eslint-disable-next-line no-unused-expressions
          response?.content?.length > 0
            ? setAccountCurrentTitles(response.content)
            : setAccountCurrentTitles([]);
          return response;
        },
      ),
    [account?.accountId],
  );
  const addProductToBasket = useCallback(
    (product) => {
      const hasRestriction = product?.payload?.some((item) =>
        item.restrictions.some(
          (rest) =>
            rest.restriction === 'LINE_GROUPS' &&
            (rest.value1 === 1 ||
              rest.value3 === 1 ||
              rest.value5 === 1 ||
              rest.value7 === 1),
        ),
      );
      if (hasRestriction) {
        const whereIsRestrictionData = product.payload.map((item) => {
          const results = [];
          item.restrictions.forEach((rest) => {
            if (
              rest.restriction === 'LINE_GROUPS' &&
              (rest.value1 === 1 ||
                rest.value3 === 1 ||
                rest.value5 === 1 ||
                rest.value7 === 1)
            ) {
              if (rest.value1 === 1) {
                results.push('value1');
              }
              if (rest.value3 === 1) {
                results.push('value3');
              }
              if (rest.value5 === 1) {
                results.push('value5');
              }
              if (rest.value7 === 1) {
                results.push('value7');
              }
            }
          });
          return results;
        });
        const titleFound = accountCurrentTitles.find(
          (title) => title.productId === product.id,
        );
        const ownedTitle = titleFound !== undefined ? titleFound : null;
        const now = new Date();
        const isDateAOutsideInterval =
          ownedTitle?.endDateA === null ||
          (ownedTitle?.endDateA != null &&
            new Date(ownedTitle?.endDateA) < now);
        const isDateBOutsideInterval =
          ownedTitle?.endDateB === null ||
          (ownedTitle?.endDateB != null &&
            new Date(ownedTitle?.endDateB) < now);
        const canAddTitleOnEveryLine =
          ownedTitle === null ||
          (isDateBOutsideInterval === true && isDateAOutsideInterval === true);
        getRouteById(titleFound.lines).then((res) => setLine(res));
        if (product?.category === 'PASS' && canAddTitleOnEveryLine === false) {
          setWhereIsRestriction(whereIsRestrictionData);
        }
        productForm.setFieldsValue({
          product: { ...product, count: 1 },
        });
      }
    },
    [accountCurrentTitles, productForm],
  );

  const handleRestrictionsError = useCallback(() => {
    message.error({
      content: t('errors.restrictionNotCompleted'),
      key: 'pos',
      duration: 3,
      className: 'card-message',
    });
  }, [t]);

  const getLineRestriction = (formInstance) => {
    return formInstance
      .getFieldValue(['product', 'payload'])
      .flatMap((payloadItem) =>
        payloadItem.restrictions.find(
          (restriction) => restriction.restriction === 'LINE_GROUPS',
        ),
      )
      .find(Boolean); // Get the first non-undefined result
  };

  const getZonalRestriction = (formInstance) => {
    const metropolitanEnabled = window._env_.METROPOLITAN_ENABLE === 'true';

    if (metropolitanEnabled === false) {
      return null;
    }
    return (
      formInstance
        .getFieldValue(['product', 'payload'])
        .map((payloadItem) =>
          payloadItem.restrictions.find(
            (restriction) => restriction.restriction === 'ZONAL',
          ),
        )
        .find(Boolean) || null
    ); // return the first non-undefined, or null
  };

  const getRestrictionsNotFilled = (formInstance) => {
    return formInstance
      .getFieldValue(['product', 'payload'])
      .flatMap((payloadItem) =>
        payloadItem.restrictions
          .filter((restriction) => restriction.restriction === 'LINE_GROUPS')
          .flatMap((restriction) =>
            MAPPING.filter(
              (mapItem) =>
                restriction[mapItem.val] === 1 && !restriction[mapItem.opt],
            ).map((mapItem) => ({
              key: mapItem.val,
              value: restriction[mapItem.val],
              option: restriction[mapItem.opt],
            })),
          ),
      );
  };

  const rechargeProduct = useCallback(
    (value) => {
      const firstLineRestriction = getLineRestriction(productForm);
      const restrictionsNotFilled = getRestrictionsNotFilled(productForm);
      if (restrictionsNotFilled?.length > 0) {
        handleRestrictionsError();
        return;
      }
      const firstZonalRestriction = getZonalRestriction(productForm);
      const restrict = firstZonalRestriction || firstLineRestriction;
      rechargeProductToOrder(shift, order, {
        id: value.id,
        count: value.count,
        restriction: restrict,
        startDate: value.startDate,
      })
        .then((res) => {
          setOrder(res);
          setCurrentProduct();
          productForm.resetFields();
        })
        .catch((err) => {
          console.error(err);
          message.error({
            content: err.inner?._
              ? t(
                  err.inner._,
                  err.inner.logicalIds
                    ? {
                        logicalIds: err.inner.logicalIds
                          .split(',')
                          .map((id) => id.split('-').pop().trim())
                          .join('\n'),
                      }
                    : undefined,
                )
              : t('entity.pos.errors.productNotAdded'),
            key: 'pos',
            duration: 5,
            className: 'card-message',
          });
        });
    },
    [productForm, shift, order, setOrder, t, handleRestrictionsError],
  );

  const enableCard = useCallback(() => {
    setSeeEnableCard(false);
    enableOrderCard(shift, order)
      .then(() => {
        message.success({
          content: t('entity.pos.successMessages.cardUnblocked'),
          key: 'pos',
          className: 'card-message',
        });
        setCardBlocked(false);
      })
      .catch((err) => {
        console.error(err);
        notification.error({
          message: err.message,
        });
      });
  }, [shift, order, t, setCardBlocked]);

  const { content, handleChange } = useDatasource(fetchTitles);

  const changeCard = useCallback(() => {
    changeCardToOrder(shift, order)
      .then((res) => {
        setOrder(res);
      })
      .catch((err) => {
        console.error(err);
        message.error({
          content: err.inner._
            ? t(err.inner._)
            : t('entity.pos.errors.productNotAdded'),
          key: 'pos',
          duration: 1,
          className: 'card-message',
        });
      });
  }, [order, setOrder, shift, t]);

  useEffect(() => {
    const arr = (attachments || []).map((item) => ({
      ...item,
      docTypeName: !docTypes.loading
        ? docTypes.content.find((item1) => item.documentId === item1.id).name
        : null,
      expirationDate: item.expirationDate ? moment(item.expirationDate) : null,
    }));
    const type = userTypes.content.find(
      // eslint-disable-next-line eqeqeq
      (c) => c.id == account.userProfileTypeId,
    );
    const docs = type?.docTypes || [];

    const res = docTypes.content.filter((dt) => docs.includes(dt.id));
    form.setFieldsValue({
      userTypeId: account.userProfileTypeId,
      files:
        attachments && attachments.length
          ? arr
          : res
              .filter((r) => !r.deleted)
              .map((r) => ({
                hasExpirationDate: r.hasExpirationDate,
                dateChangeable: r.dateChangeable,
                durationType: r.durationType,
                documentId: r.id,
                ducumentName: null,
                docTypeName: r.name,
                initial: false,
                expirationDate: r.expirationDate
                  ? moment(r.expirationDate)
                  : null,
              })),
    });
  }, [docTypes, attachments, form, userTypes, account]);

  const handleSeeEnableCard = useCallback(() => setSeeEnableCard(true), []);

  const handleExtendProduct = useCallback(
    (product) => {
      const req = {
        id: product.id,
        count: product?.productType === 'PURSE' ? 100 : 1,
      };

      rechargeProductToOrder(shift, order, req)
        .then((res) => {
          setOrder(res);
        })
        .catch((err) => {
          console.error(err);
          message.error({
            content: err.inner._
              ? t(err.inner._)
              : t('entity.pos.errors.productNotAdded'),
            key: 'pos',
            duration: 5,
            className: 'card-message',
          });
        });
    },
    [shift, order, setOrder, t],
  );

  const handleChangeProduct = useCallback(
    (product) => {
      getProductById(product.productId)
        .then((value) => {
          if (value.validityStartType === 'FUTURE') {
            const selectedTitle = availableProductsList
              .map((item) => item.products)
              .flat()
              .find((el) => {
                const matchingTitleData = el.payload.find(
                  (payloadEl) => payloadEl.id === product.productId,
                );
                return matchingTitleData !== undefined;
              });
            setCurrentProduct(value);
            setExtendedTitle(product);
            addProductToBasket(selectedTitle);
            productForm.setFieldsValue({
              id: product.id,
              count: value.category === 'PASS' ? null : 1,
              product: selectedTitle,
            });
          } else {
            handleExtendProduct(product);
          }
        })
        .catch((err) => {
          console.error(err);
          message.error({
            content: err.inner._
              ? t(err.inner._)
              : t('entity.pos.errors.productNotAdded'),
            key: 'pos',
            duration: 5,
            className: 'card-message',
          });
        });
    },
    [
      availableProductsList,
      addProductToBasket,
      productForm,
      handleExtendProduct,
      t,
    ],
  );

  const [uatsDictionary, setUatsDictionary] = useState({
    content: [],
    loading: false,
    error: false,
  });

  const metropolitanEnabled = useMemo(
    () => window._env_.METROPOLITAN_ENABLE === 'true',
    [],
  );

  useEffect(() => {
    let cancelled = false;

    if (!metropolitanEnabled) {
      setUatsDictionary({
        content: [],
        loading: false,
        error: false,
      });

      return () => {
        cancelled = true;
      };
    }

    setUatsDictionary({
      content: [],
      loading: true,
      error: false,
    });

    setUatsDictionary({
      content: [],
      loading: true,
      error: false,
    });

    const loadUats = async () => {
      try {
        const data = await getAdminUnits();
        if (!cancelled) {
          setUatsDictionary({
            content: data,
            loading: false,
            error: false,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setUatsDictionary({
            content: [],
            loading: false,
            error: true,
          });
        }
      }
    };

    loadUats();

    return () => {
      cancelled = true;
    };
  }, [metropolitanEnabled]);

  const existentColumns = useMemo(() => {
    return [
      Column.text(
        'productName',
        t('entity.account.transportTitle.product_name'),
        {
          filter: true,
        },
      ),
      Column.text(
        'productType',
        t('entity.account.transportTitle.product_types._'),
        {
          filter: true,
        },
      ),
      Column.text(
        'validPeriod',
        t('entity.account.transportTitle.validPeriod'),
        {
          sort: false,
          width: 500,
        },
      ),
      Column.text('quantity', t('entity.account.transportTitle.quantity'), {
        sort: false,
      }),
      Column.text('lines', t('entity.account.transportTitle.lines'), {
        sort: false,
      }),
      ...(metropolitanEnabled
        ? [
            Column.dictionary(
              'uatStart',
              t('components.salesReport.uatStart'),
              uatsDictionary,
              {
                filter: false,
              },
            ),
            Column.dictionary(
              'uatStop',
              t('components.salesReport.uatStop'),
              uatsDictionary,
              {
                filter: false,
              },
            ),
          ]
        : []),
      {
        title: t('entity.pos.actions.actions'),
        key: 'actions',
        width: 1,
        render: (_, row) => (
          <Space>
            <Button type="danger" onClick={() => handleChangeProduct(row)}>
              {t('entity.pos.actions.renewal')}
            </Button>
          </Space>
        ),
      },
    ];
  }, [handleChangeProduct, t, metropolitanEnabled, uatsDictionary]);

  const handleClickNewProducts = useCallback(() => {
    setOrder({ ...order, account: { ...order.account, userId: null } });
  }, [order, setOrder]);

  const handleClickEduInfo = useCallback(() => {
    getEduInfo(account.userIdentityCard)
      .then((res) => {
        setEduInfo(res);
      })
      .catch((err) => {
        setEduInfo({ studyStatus: t(err.inner._) });
        message.error({
          content: t(err.inner._),
          key: 'pos',
          duration: 3,
          className: 'card-message',
        });
      });
  }, [account.userIdentityCard, t]);

  const removeProductFromBasket = useCallback(
    (product) => {
      removeProductFromOrder(shift, order, product)
        .then((res) => {
          setOrder(res);
        })
        .catch((err) => {
          console.error(err);
          message.error({
            content: t('entity.pos.errors.productNotRemoved'),
            key: 'pos',
            duration: 5,
            className: 'card-message',
          });
        });
    },
    [shift, order, setOrder, t],
  );

  const getRestrictionsList = useCallback((product) => {
    return ['option1', 'option2', 'option3', 'option4']
      .map(
        (key) =>
          product?.payload
            .filter((prod) => prod.restrictions != null)
            .flatMap((prod) => prod?.restrictions)
            .find((rest) => rest.restriction === 'LINE_GROUPS')?.[key],
      )
      .filter((v) => v != null)
      .join(',');
  }, []);

  const getUatName = useCallback(
    (option) => {
      if (!option || uatsDictionary.loading || uatsDictionary.error) return '';

      const match = uatsDictionary.content.find((u) => u.id === option);

      return match?.name ?? '';
    },
    [uatsDictionary],
  );

  const getZonalRestrictionNames = useCallback(
    (product, optionKeys) => {
      const keys = Array.isArray(optionKeys) ? optionKeys : [optionKeys];

      const zonal = product?.payload
        ?.flatMap((p) => p.restrictions || [])
        ?.find((r) => r.restriction === 'ZONAL');

      if (!zonal) return '';

      return keys
        .map((key) => getUatName(zonal[key]))
        .filter(Boolean)
        .join(', ');
    },
    [getUatName],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('entity.pos.existingCard.selectedProducts.productName'),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: t('entity.pos.existingCard.selectedProducts.price'),
        width: 150,
        key: 'price',
        dataIndex: 'price',
        render: (_, row) =>
          row.category === 'PURSE'
            ? (row.qty * 0.01).toFixed(2)
            : (row.price * 0.01).toFixed(2),
      },
      {
        title: t('entity.pos.existingCard.selectedProducts.quantity'),
        width: 150,
        dataIndex: 'qty',
        key: 'count',
        render: (_, row) => (row.category === 'PURSE' ? '-' : row.qty),
      },
      {
        title: t('entity.pos.existingCard.selectedProducts.lines'),
        width: 150,
        dataIndex: 'lines',
        key: 'lines',
        render: (_, row) => getRestrictionsList(row),
      },
      ...(metropolitanEnabled
        ? [
            {
              title: t('components.salesReport.uatStart'),
              width: 150,
              dataIndex: 'lines',
              key: 'lines',
              render: (_, row) => getZonalRestrictionNames(row, 'option1'),
            },
            {
              title: t('components.salesReport.uatStop'),
              width: 150,
              dataIndex: 'lines',
              key: 'lines',
              render: (_, row) => getZonalRestrictionNames(row, 'option2'),
            },
          ]
        : []),
      {
        title: t('entity.pos.existingCard.selectedProducts.totalPrice'),
        width: 150,
        key: 'totalPrice',
        render: (_, row) => <>{(row.qty * row.price * 0.01).toFixed(2)}</>,
      },

      {
        title: t('entity.pos.existingCard.selectedProducts.validityPeriod'),
        width: 200,
        dataIndex: 'validPeriod',
        key: 'validPeriod',
      },
      {
        title: t('entity.pos.actions.actions'),
        key: 'actions',
        width: 1,
        render: (_, row) => (
          <Space>
            <Button type="primary" disabled>
              {t('entity.pos.actions.configuration')}
            </Button>
            <Button type="danger" onClick={() => removeProductFromBasket(row)}>
              {t('entity.pos.actions.delete')}
            </Button>
          </Space>
        ),
      },
    ];
  }, [
    t,
    removeProductFromBasket,
    getRestrictionsList,
    getZonalRestrictionNames,
    metropolitanEnabled,
  ]);

  const handleCancelOrder = useCallback(() => {
    cancelOrder(shift, order)
      .then((res) => {
        setOrder(res);
      })
      .catch((err) => {
        console.error(err);
        message.error({
          content: t('entity.pos.errors.cancelOrderError'),
          key: 'pos',
          duration: 5,
          className: 'card-message',
        });
      });
  }, [shift, order, setOrder, t]);

  const reason = useRef(null);
  const oldCardPosesion = useRef(false);

  const handleBlockCard = useCallback(() => {
    Modal.confirm({
      title: t('entity.pos.existingCard.blockCard'),
      content: (
        <>
          <div>
            <Typography.Paragraph>
              {t('pages.stocks.reason')} :
            </Typography.Paragraph>
            <Select
              style={{ width: '100%' }}
              onChange={(val) => {
                reason.current = val;
              }}
              defaultValue={null}
            >
              {(blacklistReasons.content || [])
                .filter(
                  (it) =>
                    it.blockingPlaces &&
                    it.blockingPlaces.includes(BlockingPlaces.POS),
                )
                .map((res) => (
                  <Select.Option key={res.id} value={res.id}>
                    {res.name}
                  </Select.Option>
                ))}
            </Select>
          </div>
          <br />
          <div>
            {isStocksEnabled && ArticleTypes.Mifare1K === card?.cardTypeId && (
              <>
                {t('pages.stocks.hasCard')}
                <Checkbox
                  onChange={(e) => {
                    oldCardPosesion.current = e.target.checked;
                  }}
                />
              </>
            )}
          </div>
        </>
      ),
      async onOk() {
        if (reason.current === null || reason.current === undefined) {
          message.error({
            content: t('entity.pos.errors.selectBlockReason'),
            key: 'pos',
            duration: 5,
            className: 'card-message',
          });
          return Promise.reject();
        }
        return blockCard(shift, order, reason.current, oldCardPosesion.current)
          .then(() => {
            message.success({
              content: t('entity.pos.successMessages.cardBlocked'),
              key: 'pos',
              className: 'card-message',
            });
            setOrder(null);
          })
          .catch((err) => {
            console.error(err);
            message.error({
              content: t('entity.pos.errors.blockCardError'),
              key: 'pos',
              duration: 5,
              className: 'card-message',
            });
            throw err; // Re-throw to prevent modal from closing on error
          });
      },
      onCancel() {},
    });
  }, [
    order,
    setOrder,
    shift,
    blacklistReasons,
    isStocksEnabled,
    card?.cardTypeId,
    t,
  ]);

  const handleExchangeCard = useCallback(() => {
    Modal.confirm({
      title: t('entity.pos.changeCard'),
      content: (
        <div>
          {isStocksEnabled && ArticleTypes.Mifare1K === card?.cardTypeId && (
            <>
              {t('pages.stocks.hasCard')}
              <Checkbox
                onChange={(e) => {
                  oldCardPosesion.current = e.target.checked;
                }}
              />
            </>
          )}
        </div>
      ),
      onOk() {
        changeCardToOrder(shift, order, oldCardPosesion.current)
          .then((res) => {
            setOrder(res);
          })
          .catch((err) => {
            console.error(err);
            message.error({
              content: err.inner._
                ? t(err.inner._)
                : t('entity.pos.errors.productNotAdded'),
              key: 'pos',
              duration: 1,
              className: 'card-message',
            });
          });
      },
      onCancel() {},
    });
  }, [order, setOrder, shift, isStocksEnabled, card?.cardTypeId, t]);

  const nextStep = useCallback(() => {
    moveOrderToNextStep(shift, order)
      .then((res) => {
        setOrder(res);
      })
      .catch((err) => {
        console.error(err);
        message.error({
          content: err.inner._
            ? t(err.inner._)
            : t('entity.pos.errors.errorNextStep'),
          key: 'pos',
          duration: 1,
          className: 'card-message',
        });
      });
  }, [shift, order, setOrder, t]);

  const changeAttachments = useCallback(() => {
    const value = attachmentForm;
    changeAttachmentsToOrder(shift, order, {
      ...value,
      files: value.files,
    })
      .then((res) => {
        setOrder(res);
        nextStep();
      })
      .catch((err) => {
        console.error(err);
        message.error({
          content: err.inner._
            ? t(err.inner._)
            : t('entity.pos.errors.productNotAdded'),
          key: 'pos',
          duration: 5,
          className: 'card-message',
        });
      });
  }, [attachmentForm, nextStep, order, setOrder, shift, t]);

  const handleNext = useCallback(() => {
    if (card.isNominal) {
      const phoneNumber = attachmentForm?.userPhone;
      if (!(phoneNumber && phoneRegex.test(phoneNumber))) {
        message.error({
          content: t('entity.pos.errors.invalidPhoneNumber'),
          key: 'pos',
          duration: 5,
          className: 'card-message',
        });
        return;
      }
      changeAttachments();
    } else {
      nextStep();
    }
  }, [
    card.isNominal,
    attachmentForm?.userPhone,
    phoneRegex,
    changeAttachments,
    t,
    nextStep,
  ]);

  return (
    <div className="pos">
      <div className="pos__wrapper">
        <Descriptions bordered>
          <Descriptions.Item label={t('entity.pos.existingCard.fullName')}>
            {account.userFullname}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('entity.pos.existingCard.identityNumber')}
          >
            {account.userIdentityCard}
          </Descriptions.Item>
          <Descriptions.Item label={t('entity.pos.existingCard.cardNumber')}>
            {card.cardLogicalId}
          </Descriptions.Item>

          <Descriptions.Item
            label={t('entity.pos.existingCard.individualPerson')}
          >
            {account.userCustomerType}
          </Descriptions.Item>
          <Descriptions.Item label={t('entity.pos.existingCard.userType')}>
            {userType?.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('entity.pos.existingCard.accountType')}>
            &nbsp;
          </Descriptions.Item>
        </Descriptions>
        <Row
          gutter={{ xs: 8, sm: 16, md: 24, lg: 28, xl: 0 }}
          style={{ padding: '2rem 0' }}
        >
          <Col xs={{ span: 5 }} lg={{ span: scanEnable ? 10 : 5, offset: 0 }}>
            {account && <AccountDetailsForm />}
          </Col>
          <Col xs={{ span: 7 }} lg={{ span: scanEnable ? 4 : 7, offset: 4 }}>
            {!cardBlocked && (
              <>
                <POSButton
                  title={t('entity.pos.productSelection')}
                  onClick={handleClickNewProducts}
                  color="green"
                />
                {isEduEnabled && environment === Environments.PITESTI && (
                  <>
                    <div style={{ marginTop: '0.5rem' }}>
                      <POSButton
                        title={t('entity.pos.eduInfo.eduInfo')}
                        onClick={handleClickEduInfo}
                        color="green"
                      />
                    </div>
                    <EduInfoCard eduInfo={eduInfo} />
                  </>
                )}
              </>
            )}
          </Col>
          <Col
            xs={{ offset: 12, span: 12 }}
            lg={{ span: scanEnable ? 2 : 4, offset: 4 }}
          >
            {!cardBlocked ? (
              <Row gutter={[0, 8]}>
                <Col span={24}>
                  <POSButton
                    title={t('entity.pos.cardReplacement.cardReplacement')}
                    onClick={
                      isStocksEnabled &&
                      ArticleTypes.Mifare1K === card?.cardTypeId
                        ? handleExchangeCard
                        : changeCard
                    }
                    color="green"
                  />
                </Col>
                <Col span={24}>
                  <POSButton
                    title={t('entity.pos.existingCard.blockCard')}
                    onClick={handleBlockCard}
                    color="red"
                  />
                </Col>
              </Row>
            ) : (
              <Col span={24}>
                <POSButton
                  hidden={
                    !(
                      blacklistReasons.content.find(
                        (item) => item.id === order.card.cardBlacklistReason,
                      )?.unblockingPlaces || []
                    ).includes(UnblockingPlaces.POS)
                  }
                  title={t('entity.pos.existingCard.unblockCard')}
                  onClick={handleSeeEnableCard}
                  color="green"
                />
              </Col>
            )}
          </Col>
        </Row>

        <Row
          gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}
          style={{ padding: '2rem 0' }}
        >
          <Col span={12}>
            <Divider>
              {t('entity.pos.existingCard.selectedProducts.selectedProducts')}
            </Divider>
            <div className="pos__table">
              <Table
                dataSource={order.products}
                rowKey="id"
                columns={columns}
                pagination={false}
              />
            </div>
          </Col>
          <Col span={12}>
            <Divider>{t('entity.pos.existingCard.existingTitles')}</Divider>
            <div className="pos__table">
              <Table
                columns={existentColumns}
                rowKey="id"
                // loading={loading}
                dataSource={content}
                onChange={handleChange}
                pagination={false}
              />
            </div>
          </Col>
        </Row>
        <Modal
          title={t('entity.pos.existingCard.unblockCard')}
          visible={seeEnableCard}
          closable={false}
          footer={[
            <Button key="submit" type="primary" onClick={enableCard}>
              {t('entity.pos.existingCard.unblockCardAction')}
            </Button>,
            <Button
              key="close"
              type="primary"
              onClick={() => {
                setSeeEnableCard(false);
              }}
            >
              {t('actions.close')}
            </Button>,
          ]}
        >
          {t('entity.pos.existingCard.unblockQuestion')}
        </Modal>
      </div>
      <div className="pos__actions">
        <div>
          <POSButton
            title={t('entity.pos.actions.cancel')}
            color="red"
            size="small"
            onClick={() => {
              setCardBlocked(false);
              handleCancelOrder();
            }}
          />
        </div>

        <div>
          {!cardBlocked && (
            <POSButton
              title={t('entity.pos.actions.next')}
              color="green"
              size="small"
              onClick={handleNext}
            />
          )}
        </div>
      </div>
      <Modal
        title={t('entity.pos.actions.configuration')}
        visible={currentProduct}
        onOk={() => {
          productForm
            .validateFields()
            .then(rechargeProduct)
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
        onCancel={() => {
          setCurrentProduct();
          setExtendedTitle();
          productForm.resetFields();
        }}
      >
        <Form form={productForm} layout="vertical">
          <Form.Item hidden name={['id']} />
          <Form.Item hidden name={['count']} />
          {currentProduct && currentProduct.validityStartType === 'FUTURE' && (
            <Form.Item noStyle name={['startDate']}>
              <DatePickerLocale
                format="DD.MM.YYYY HH:mm"
                showTime={{
                  defaultValue: dayjs('00:00:00', 'HH:mm'),
                }}
                style={{ width: '100%' }}
                disabledDate={disabledDate}
              />
            </Form.Item>
          )}
          <Form.List name={['product', 'payload']}>
            {(fields) =>
              fields.map((field) => (
                <Form.List name={[field.name, 'restrictions']} key={field.key}>
                  {(fields2) =>
                    fields2.map((field2) => (
                      <Restriction
                        key={field2.key}
                        form={productForm}
                        line={line?.id}
                        whereIsRestriction={whereIsRestriction}
                        name={[field.name, 'restrictions', field2.name]}
                        title={extendedTitle}
                      />
                    ))
                  }
                </Form.List>
              ))
            }
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default AccountDetails;
